/**
 * Calculate the current workflow step based on charter request data
 * @param {Object} charterRequest - The charter request object
 * @returns {number} - Current workflow step (1-12)
 */
export const calculateWorkflowStep = (charterRequest) => {
  if (!charterRequest) return 1;
  
  // Special handling for step 10: if admin has completed inspection, 
  // users should move to step 11 to sign charter party agreement
  if (charterRequest.step === 10 && charterRequest.vesselInspected?.inspected === true) {
    return 11; // Users should move to step 11 to sign charter party agreement
  }
  
  // Prioritize API's step field if available
  if (charterRequest.step !== undefined) {
    return charterRequest.step;
  }

  // Helper functions to check status
  const isAdminApproved = () => {
    return charterRequest.seenByAdmin === true || 
           charterRequest.adminApproved === true ||
           charterRequest.status === "approved";
  };

  const hasVesselOwnerResponse = () => {
    return charterRequest.vesselOwnerResponse?.processed === true ||
           charterRequest.vesselOwnerResponse?.processed === false ||
           charterRequest.canProvideSecurity?.processed === true ||
           charterRequest.canProvideSecurity?.processed === false;
  };

  const isChartererMovingForward = () => {
    return charterRequest.isChartererMovingForward === true ||
           charterRequest.chartererMovingForward === true;
  };

  const isChartererAcceptingPrice = () => {
    return charterRequest.acceptPrice === true;
  };

  const isChartererDeclining = () => {
    return charterRequest.processed === false && charterRequest.userMessage;
  };

  const hasMeetingDatesSelected = () => {
    return charterRequest.meetingDatesSelected === true ||
           charterRequest.meetingDate1 ||
           charterRequest.meetingDate2;
  };

  const hasVesselNominationSigned = () => {
    return charterRequest.vesselNominationSigned === true ||
           charterRequest.vesselNominationAgreement?.signed === true;
  };

  const hasVesselInspectionCompleted = () => {
    return charterRequest.vesselInspectionCompleted === true ||
           charterRequest.inspectionResult === true ||
           charterRequest.vesselInspected?.inspected === true;
  };

  const hasCharterPartySigned = () => {
    return charterRequest.charterPartySigned === true ||
           charterRequest.charterPartyAgreement?.signed === true;
  };

  // Step calculation logic based on the 12-step workflow
  if (!isAdminApproved()) {
    return 2; // Step 2: processCharterRequest (admin)
  }

  if (!hasVesselOwnerResponse()) {
    return 3; // Step 3: charterResponse (vessel owner)
  }

  if (isChartererDeclining()) {
    return 4; // Step 4: processCharterResponse (admin)
  }

  if (!isChartererMovingForward()) {
    return 5; // Step 5: isCharterMovingForward (charterer)
  }

  if (!isChartererAcceptingPrice()) {
    return 6; // Step 6: processCharterMovingForward (admin) - when acceptPrice is false
  }

  if (!hasMeetingDatesSelected()) {
    return 7; // Step 7: selectMeetingDate (charterer and vessel owner)
  }

  if (!hasVesselNominationSigned()) {
    return 9; // Step 9: signVesselNominationAgreement (charterer and vessel owner)
  }

  if (!hasVesselInspectionCompleted()) {
    return 10; // Step 10: processVesselInspectionResult (admin)
  }

  if (!hasCharterPartySigned()) {
    return 11; // Step 11: signCharterPartyAgreement (charterer and vessel owner)
  }

  return 12; // Step 12: processCharterPartyAgreement (admin) - completed
};

/**
 * Get the next action required based on current step and user type
 * @param {number} currentStep - Current workflow step
 * @param {string} userType - 'vesselOwner' or 'charterer'
 * @returns {Object} - Next action information
 */
export const getNextAction = (currentStep, userType) => {
  // Map API step to display step (12-step workflow)
  const getDisplayStep = (apiStep) => {
    switch (apiStep) {
      case 1: return 1; // charterRequest => charterer
      case 2: return 2; // processCharterRequest => admin
      case 3: return 3; // charterResponse => vessel owner
      case 4: return 4; // processCharterResponse => admin
      case 5: return 5; // isCharterMovingForward => charterer
      case 6: return 6; // processCharterMovingForward => admin (if acceptPrice false)
      case 7: return 7; // selectMeetingDate => charterer and vessel owner
      case 8: return 8; // meeting result saved to database => system
      case 9: return 9; // signVesselNominationAgreement => charterer and vessel owner
      case 10: return 10; // processVesselInspectionResult => admin
      case 11: return 11; // signCharterPartyAgreement => charterer and vessel owner
      case 12: return 12; // processCharterPartyAgreement => admin
      default: return apiStep;
    }
  };

  const displayStep = getDisplayStep(currentStep);

  const actions = {
    vesselOwner: {
      1: { title: "Charter Request Created", description: "Charterer has created the request", action: null },
      2: { title: "Waiting for Admin Approval", description: "Admin is processing the charter request", action: null },
      3: { title: "Send Response", description: "Provide your pricing and terms for this charter request", action: "sendResponse" },
      4: { title: "Waiting for Charterer Decision", description: "Charterer is deciding whether to proceed", action: null },
      5: { title: "Waiting for Admin Approval", description: "Admin is processing charterer's decision", action: null },
      6: { title: "No Action Required", description: "Awaiting next step", action: null },
      7: { title: "Select Meeting Date", description: "Choose meeting dates with the charterer", action: "selectMeetingDates" },
      8: { title: "Sign Vessel Nomination Agreement", description: "Sign the vessel nomination agreement", action: "signVesselNomination" },
      9: { title: "Sign Vessel Nomination Agreement", description: "Sign the vessel nomination agreement", action: "signVesselNomination" },
      10: { title: "Waiting for Inspection Result", description: "Admin is processing vessel inspection result", action: null },
      11: { title: "Sign Charter Party Agreement", description: "Sign the charter party agreement", action: "signCharterPartyAgreement" },
      12: { title: "Process Completed", description: "All agreements have been signed and the charter request is now complete!", action: null }
    },
    charterer: {
      1: { title: "Charter Request Created", description: "You have created the charter request. Waiting for admin to review.", action: null },
      2: { title: "Waiting for Admin Approval", description: "Admin is processing your charter request. We'll notify you once it's approved.", action: null },
      3: { title: "Waiting for Vessel Owner Response", description: "Vessel owner is reviewing your request and preparing their response.", action: null },
      4: { title: "Move Forward", description: "Review the vessel owner's response and decide whether to proceed with this charter request.", action: "moveForward" },
      5: { title: "Waiting for Admin Processing", description: "Admin is processing your decision. This will be reviewed shortly.", action: null },
      6: { title: "Select Meeting Date", description: "Choose meeting dates with the vessel owner to discuss charter details.", action: "selectMeetingDates" },
      7: { title: "Meeting Dates Selected", description: "Meeting dates have been selected. Waiting for both parties to confirm.", action: null },
      8: { title: "Sign Vessel Nomination Agreement", description: "Sign the vessel nomination agreement to proceed with vessel selection and booking.", action: "signVesselNomination" },
      9: { title: "Sign Vessel Nomination Agreement", description: "Sign the vessel nomination agreement to proceed with vessel selection and booking.", action: "signVesselNomination" },
      10: { title: "Waiting for Inspection Result", description: "Admin is processing vessel inspection result. We'll notify you when it's ready.", action: null },
      11: { title: "Sign Charter Party Agreement", description: "Sign the charter party agreement to finalize the charter terms and conditions.", action: "signCharterPartyAgreement" },
      12: { title: "Process Completed", description: "All agreements have been signed and the charter request is now complete!", action: null }
    }
  };

  return actions[userType]?.[displayStep] || {
    title: "Unknown Step",
    description: "Please contact support",
    action: null
  };
};

/**
 * Check if a specific step is completed
 * @param {Object} charterRequest - The charter request object
 * @param {number} step - Step number to check
 * @returns {boolean} - Whether the step is completed
 */
export const isStepCompleted = (charterRequest, step) => {
  if (!charterRequest) return false;

  // Helper function to check if admin has approved
  const isAdminApproved = () => {
    return charterRequest.adminApproved === true || 
           charterRequest.adminApproved === "true" || 
           charterRequest.status === "approved" ||
           charterRequest.step >= 1;
  };

  // Helper function to check if vessel owner has responded
  const hasVesselOwnerResponse = () => {
    return charterRequest.vesselOwnerResponse && 
           (charterRequest.vesselOwnerResponse.processed || 
            charterRequest.vesselOwnerResponse.canProvideSecurity);
  };

  switch (step) {
    case 0:
      return true; // Always completed if we have a request
    case 1:
      return isAdminApproved();
    case 2:
      return hasVesselOwnerResponse();
    case 3:
      return !!charterRequest.adminResponseReview;
    case 4:
      return !!charterRequest.chartererMovingForward;
    case 5:
      return !!charterRequest.adminMovingForwardApproval;
    case 6:
      return !!charterRequest.meetingDatesSelected;
    default:
      return false;
  }
};

/**
 * Get step status for display
 * @param {Object} charterRequest - The charter request object
 * @param {number} step - Step number
 * @returns {string} - 'completed', 'active', or 'pending'
 */
export const getStepStatus = (charterRequest, step) => {
  const currentStep = calculateWorkflowStep(charterRequest);
  
  if (isStepCompleted(charterRequest, step)) {
    return 'completed';
  }
  
  if (step === currentStep) {
    return 'active';
  }
  
  return 'pending';
}; 